import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Chip, TextField, CircularProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import type { RouteData } from '../../types';
import './RealisticPowerCalculator.css'; // Reuse the same CSS

// Debug flag - set to false to disable debug logging
const DEBUG = false;

// Props interface for display-only power duration component
export interface PowerDisplayProps {
  routeData: RouteData;
  // Current calculated values from the hidden calculator
  calculatedDuration: number | null;
  powerToWeight: number;
  showOriginal: boolean; // Auto mode: true = show BRouter duration, false = show calculated duration
  averageSpeed: number | null;
  speedChartData: any[];
  // Callbacks when user changes settings
  onPowerToWeightChange: (newValue: number) => void;
  onAutoModeChange: (autoMode: boolean) => void;
}

// Get chart colors based on current theme
const getChartColors = () => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  // Get the accent color from CSS variables
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim();
  const errorColor = getComputedStyle(document.documentElement).getPropertyValue('--color-error').trim();
  return {
    primary: accentColor || '#1e4d40', // Use CSS variable --color-accent
    error: errorColor || '#7a1c1c', // Use CSS variable --color-error for average line
    grid: isDarkMode ? '#424242' : '#e0e0e0',
    text: isDarkMode ? '#fff' : '#000',
    axis: isDarkMode ? '#666' : '#999'
  };
};

/**
 * PowerDisplay Component
 * 
 * Display-only component that shows power-based duration calculations.
 * Does NOT perform calculations itself - receives all values as props from parent.
 * 
 * Maintains the EXACT same UI layout as RealisticPowerCalculator but without calculation logic.
 */
export const PowerDisplay: React.FC<PowerDisplayProps> = ({
  routeData,
  calculatedDuration,
  powerToWeight,
  showOriginal,
  averageSpeed,
  speedChartData,
  onPowerToWeightChange,
  onAutoModeChange
}) => {
  // Local state for dragging (temporary values during drag for smooth preview)
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const isDraggingRef = useRef<boolean>(false); // Use ref to prevent re-render issues
  const isCommittingRef = useRef<boolean>(false); // Track if we're committing a value change
  const [tempPowerToWeight, setTempPowerToWeight] = useState<number>(powerToWeight);
  const [error] = useState<string>('');
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [isEditingTextField, setIsEditingTextField] = useState<boolean>(false); // Track if user is editing the text field
  const [textFieldValue, setTextFieldValue] = useState<string>(''); // Raw text field value during editing
  
  // Chart average speed calculation
  const chartAverageSpeed = averageSpeed;

  // Debug logging
  DEBUG && console.log('PowerDisplay render:', {
    calculatedDuration,
    powerToWeight,
    showOriginal,
    averageSpeed,
    speedChartDataLength: speedChartData.length,
    routeDataDuration: routeData.duration
  });

  // Sync temp value when prop changes (except during drag or committing)
  useEffect(() => {
    DEBUG && console.log('📥 Sync effect triggered:', {
      isDragging,
      isDraggingRef: isDraggingRef.current,
      isCommitting: isCommittingRef.current,
      powerToWeight,
      tempPowerToWeight
    });
    
    if (!isDragging && !isDraggingRef.current && !isCommittingRef.current) {
      DEBUG && console.log('✅ Syncing temp value to prop:', powerToWeight);
      setTempPowerToWeight(powerToWeight);
    } else {
      DEBUG && console.log('⏭️ Skipping sync - dragging or committing');
    }
  }, [powerToWeight, isDragging]);

  // Clear committing flag after a short delay
  useEffect(() => {
    if (isCommittingRef.current) {
      const timer = setTimeout(() => {
        DEBUG && console.log('🔓 Clearing committing flag');
        isCommittingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tempPowerToWeight]);

  // Clear calculating state when new values arrive
  useEffect(() => {
    // Only clear if we're calculating AND we're not currently dragging
    if (isCalculating && !isDragging && calculatedDuration && averageSpeed) {
      DEBUG && console.log('✅ Calculation complete, clearing calculating state');
      setIsCalculating(false);
    }
  }, [calculatedDuration, averageSpeed, isCalculating, isDragging]);

  // Prevent slider from resetting when showOriginal changes during drag
  useEffect(() => {
    // Don't sync showOriginal changes while dragging
    if (isDragging) {
      return;
    }
  }, [showOriginal, isDragging]);

  // Helper function to format duration
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes} min`;
    }
    return `${minutes} min`;
  };

  return (
    <Box className="power-duration-calculator">
      {/* Always show UI if we have routeData - in Auto mode use routeData.duration, otherwise use calculatedDuration */}
      {routeData && (
        <>
          {/* Top: Results Section - Full Width */}
          <Box className="pdc-top-row">
            <Box className="pdc-results-section">
              {/* Duration Box */}
              <Box className="pdc-results-content" sx={{ minHeight: '125px', height: '125px', flexShrink: 0 }}>
                <Box className="pdc-results-header">
                  <AccessTimeIcon sx={{ fontSize: '24px' }} />
                  <Typography variant="h6" className="pdc-results-title">
                    Duration
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, minHeight: '32px', flex: 1 }}>
                  {isCalculating ? (
                    <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  ) : (
                    <Typography variant="h6" className="pdc-results-value">
                      {showOriginal 
                        ? formatDuration(routeData.duration) 
                        : formatDuration(calculatedDuration || routeData.duration)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Speed Box */}
              <Box className="pdc-results-content" sx={{ minHeight: '125px', height: '125px', flexShrink: 0 }}>
                <Box className="pdc-results-header">
                  <SpeedIcon sx={{ fontSize: '24px' }} />
                  <Typography variant="h6" className="pdc-results-title">
                    Avg Speed
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, minHeight: '32px', flex: 1 }}>
                  {isCalculating ? (
                    <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  ) : (
                    <Typography variant="h6" className="pdc-results-value">
                      {showOriginal
                        ? (routeData.duration > 0 ? ((routeData.distance / 1000) / (routeData.duration / 3600)).toFixed(1) : '0.0')
                        : (averageSpeed || 0).toFixed(1)} km/h
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

            {/* Controls Section - Full Width Slider Layout */}
            <Box className="pdc-controls-section">
              {/* Auto button */}
              <Box className="pdc-auto-container">
                <Chip
                  label={
                    <Box className="pdc-auto-label-container">
                      <TipsAndUpdatesOutlinedIcon sx={{ fontSize: '16px' }} />
                      <Box className="pdc-auto-label-text">
                        Auto
                      </Box>
                    </Box>
                  }
                  onClick={() => {
                    DEBUG && console.log('🔘 Auto button clicked, current showOriginal:', showOriginal);
                    // Set committing flag to prevent sync from interfering
                    isCommittingRef.current = true;
                    onAutoModeChange(!showOriginal);
                    // Clear flag after a delay
                    setTimeout(() => {
                      isCommittingRef.current = false;
                    }, 100);
                  }}
                  variant={showOriginal ? 'filled' : 'outlined'}
                  size="small"
                  className="pdc-auto-chip"
                />
              </Box>

              {/* Slider container */}
              <Box className={`pdc-slider-container ${showOriginal ? 'auto-mode' : ''}`}>
                <Box className="pdc-slider-track">
                  {(() => {
                    const currentValue = isDragging ? tempPowerToWeight : powerToWeight;
                    // Check if value is out of range for visual styling only
                    const valueOutOfRange = currentValue < 1.0 || currentValue > 4.0;
                    // When in auto mode, position slider at minimum (far left)
                    // Clamp the display value to slider range to ensure it works even when text field is out of range
                    let displayValue = showOriginal ? 1.0 : currentValue;
                    displayValue = Math.max(1.0, Math.min(4.0, displayValue));
                    
                    return (
                      <input
                        type="range"
                        min="1.0"
                        max="4.0"
                        step="0.05"
                        value={displayValue}
                        onMouseDown={(e) => {
                          // Disable auto mode and start dragging
                          DEBUG && console.log('🖱️ Mouse down - starting drag');
                          isDraggingRef.current = true;
                          setIsDragging(true);
                          setIsCalculating(true); // Show calculating indicator during drag
                          setIsEditingTextField(false); // Exit text field editing mode
                          
                          // Calculate the clicked value from mouse position
                          const rect = (e.target as HTMLInputElement).getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const percentage = clickX / rect.width;
                          const min = 1.0;
                          const max = 4.0;
                          const clickedValue = Math.max(min, Math.min(max, min + percentage * (max - min)));
                          const roundedValue = Math.round(clickedValue / 0.05) * 0.05; // Round to step
                          
                          setTempPowerToWeight(roundedValue);
                          
                          if (showOriginal) {
                            DEBUG && console.log('🔄 Disabling auto mode');
                            onAutoModeChange(false);
                          }
                        }}
                        onTouchStart={(e) => {
                          // Disable auto mode and start dragging (mobile)
                          DEBUG && console.log('👆 Touch start - starting drag');
                          isDraggingRef.current = true;
                          setIsDragging(true);
                          setIsCalculating(true); // Show calculating indicator during drag
                          setIsEditingTextField(false); // Exit text field editing mode
                          
                          // Calculate the touched value from touch position
                          const rect = (e.target as HTMLInputElement).getBoundingClientRect();
                          const touchX = e.touches[0].clientX - rect.left;
                          const percentage = touchX / rect.width;
                          const min = 1.0;
                          const max = 4.0;
                          const touchedValue = Math.max(min, Math.min(max, min + percentage * (max - min)));
                          const roundedValue = Math.round(touchedValue / 0.05) * 0.05; // Round to step
                          
                          setTempPowerToWeight(roundedValue);
                          
                          if (showOriginal) {
                            DEBUG && console.log('🔄 Disabling auto mode');
                            onAutoModeChange(false);
                          }
                        }}
                        onInput={(e) => {
                          // Update temp value during drag for smooth preview
                          if (isDraggingRef.current) {
                            const newValue = parseFloat((e.target as HTMLInputElement).value);
                            DEBUG && console.log('🎯 Input event - updating temp value:', newValue);
                            setTempPowerToWeight(newValue);
                          }
                        }}
                        onChange={(e) => {
                          // onChange for compatibility
                          if (isDraggingRef.current) {
                            const newValue = parseFloat(e.target.value);
                            setTempPowerToWeight(newValue);
                          }
                        }}
                        onMouseUp={() => {
                          if (isDraggingRef.current) {
                            DEBUG && console.log('🖱️ Mouse up - committing value:', tempPowerToWeight);
                            // Set committing flag to prevent sync from overwriting
                            isCommittingRef.current = true;
                            // Keep calculation state active - will be cleared when real values arrive
                            // Apply the temporary value (debounced in parent)
                            onPowerToWeightChange(tempPowerToWeight);
                            isDraggingRef.current = false;
                            setIsDragging(false);
                          }
                        }}
                        onTouchEnd={() => {
                          if (isDraggingRef.current) {
                            DEBUG && console.log('👆 Touch end - committing value:', tempPowerToWeight);
                            // Set committing flag to prevent sync from overwriting
                            isCommittingRef.current = true;
                            // Keep calculation state active - will be cleared when real values arrive
                            // Apply the temporary value (debounced in parent)
                            onPowerToWeightChange(tempPowerToWeight);
                            isDraggingRef.current = false;
                            setIsDragging(false);
                          }
                        }}
                        className={`pdc-slider ${valueOutOfRange ? 'value-out-of-range' : ''} ${showOriginal ? 'auto-mode' : ''}`}
                        disabled={false}
                      />
                    );
                  })()}
                  
                </Box>
                
                <Box className="pdc-slider-labels">
                  {[1.5, 2.0, 2.5, 3.0, 3.5].map((value) => {
                    const position = ((value - 1.0) / (4.0 - 1.0)) * 100;
                    return (
                      <span 
                        key={value}
                        style={{ left: `${position}%` }}
                      >
                        {value.toFixed(1)}
                      </span>
                    );
                  })}
                </Box>
              </Box>

              {/* Power input field */}
              <Box className="pdc-power-input-container">
                <TextField
                  type={showOriginal ? "text" : "number"}
                  value={showOriginal ? "auto" : (isEditingTextField ? textFieldValue : (isDragging ? tempPowerToWeight.toFixed(2) : powerToWeight.toFixed(2)))}
                  onFocus={(e) => {
                    // Disable auto mode when clicking in the field
                    if (showOriginal) {
                      onAutoModeChange(false);
                    }
                    
                    // Start editing mode and store current formatted value
                    setIsEditingTextField(true);
                    setTextFieldValue(powerToWeight.toString());
                    
                    // Always select all text on focus
                    setTimeout(() => {
                      e.target.select();
                    }, 0);
                  }}
                  onChange={(e) => {
                    // Just store the raw input value - don't trigger any recalculation
                    setTextFieldValue(e.target.value);
                  }}
                  onBlur={(e) => {
                    // Exit editing mode
                    setIsEditingTextField(false);
                    // NOW trigger recalculation with validated value
                    let newValue = Number(e.target.value);
                    if (isNaN(newValue) || newValue < 1.0) newValue = 1.0;
                    if (newValue > 10.0) newValue = 10.0;
                    onPowerToWeightChange(newValue);
                    setTempPowerToWeight(newValue);
                  }}
                  inputProps={{
                    min: 1.0,
                    max: 10.0,
                    step: 0.01,
                    style: { 
                      fontSize: '0.875rem',
                      color: showOriginal ? '#999' : 'inherit'
                    }
                  }}
                  size="small"
                  error={!!error}
                  disabled={false} // Always enabled now - onFocus will handle auto mode
                  className="pdc-power-input"
                  placeholder={showOriginal ? "Auto" : "W/kg"}
                />
                <span className="pdc-power-label">W/kg</span>
              </Box>
            </Box>

          {/* Bottom: Full-Width Chart Section */}
          {speedChartData.length > 0 && (
            <Box className="pdc-chart-section" sx={{ marginTop: '10px' }}>
              
              <Box className="pdc-chart-container" sx={{ position: 'relative' }}>
                {isCalculating && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 10
                  }}>
                    <CircularProgress size={40} sx={{ color: getChartColors().primary }} />
                  </Box>
                )}
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={speedChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke={getChartColors().grid} 
                      style={{ opacity: 0.3 }}
                    />
                    <XAxis 
                      dataKey="distance" 
                      tick={{ fontSize: 10, fill: getChartColors().text }}
                      axisLine={{ stroke: getChartColors().axis }}
                      tickLine={{ stroke: getChartColors().axis }}
                      interval={0}
                      ticks={(() => {
                        // Smart tick calculation based on distance range
                        const maxDist = Math.max(...speedChartData.map(d => parseFloat(d.distance)));
                        const minDist = Math.min(...speedChartData.map(d => parseFloat(d.distance)));
                        const distanceRange = maxDist - minDist;
                        
                        if (distanceRange === 0) return [Math.round(minDist)];
                        
                        // Determine step size based on range
                        const numTicks = 10; // Target number of ticks
                        const idealStep = distanceRange / (numTicks - 1);
                        let step;
                        
                        if (idealStep <= 0.5) {
                          step = 0.5; // 500m steps for very small ranges
                        } else if (idealStep <= 1) {
                          step = 1; // 1km steps
                        } else if (idealStep <= 2) {
                          step = 2; // 2km steps
                        } else if (idealStep <= 5) {
                          step = 5; // 5km steps
                        } else {
                          step = Math.ceil(idealStep / 5) * 5; // Round up to 5km increments
                        }
                        
                        // Start at first whole step >= minDist
                        const startTick = Math.ceil(minDist / step) * step;
                        const ticks = [];
                        
                        for (let tick = startTick; tick <= maxDist; tick += step) {
                          if (tick >= minDist) {
                            ticks.push(Math.round(tick * 10) / 10);
                          }
                        }
                        
                        DEBUG && console.log('Chart ticks:', ticks, 'step:', step, 'range:', distanceRange);
                        return ticks;
                      })()}
                      tickFormatter={(value) => `${value}km`}
                      label={{ value: 'km', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: '10px', fill: getChartColors().text } }}
                    />
                    <YAxis 
                      domain={[0, 80]}
                      ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
                      interval={0}
                      tick={{ fontSize: 10, fill: getChartColors().text }}
                      axisLine={{ stroke: getChartColors().axis }}
                      tickLine={{ stroke: getChartColors().axis }}
                      width={40}
                      tickFormatter={(value) => `${value}`}
                      label={{ value: 'km/h', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '10px', fill: getChartColors().text } }}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const displaySpeed = showOriginal ? data.brouterSpeed : data.physicsSpeed;
                          return (
                            <Box className="pdc-tooltip">
                              <Typography variant="caption" className="pdc-tooltip-speed">
                                {displaySpeed} km/h
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey={showOriginal ? "brouterSpeed" : "physicsSpeed"} 
                      fill={getChartColors().primary}
                      opacity={0.8}
                      radius={[0, 0, 0, 0]}
                      stroke={getChartColors().primary}
                      strokeWidth={1}
                    />
                    <ReferenceLine 
                      y={chartAverageSpeed || 0} 
                      stroke={getChartColors().error} 
                      strokeWidth={2}
                      strokeDasharray="none"
                      label={{ 
                        value: `⌀ ${(chartAverageSpeed || 0).toFixed(1)}`, 
                        position: 'right', 
                        style: { fontSize: '10px', fill: getChartColors().text } 
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Helper text */}
      {error && (
        <Typography variant="caption" className="pdc-error-text">
          {error}
        </Typography>
      )}
      <Typography variant="caption" className="pdc-helper-text">
        Typical Values: Beginner 2.0, Hobby 2.5, Sporty 3.0
      </Typography>
      
      <Typography variant="caption" className="pdc-description-text">
        Based on physics simulation with gradient, surface and resistances.
      </Typography>
    </Box>
  );
};
